import React, { createContext, useContext, useState, useEffect } from 'react';
import { currenciesAPI, companiesAPI } from '../services/api';
import { useAuth } from './AuthContext';
import { CONFIG, bankersRound } from '../utils/precision';

const defaultCurrency = { code: 'USD', symbol: '$', name: 'US Dollar' };

const formatAmountDefault = (amount, currency = defaultCurrency) => {
  if (!currency || amount === null || amount === undefined) return '0.00';
  const numAmount = parseFloat(amount) || 0;
  return `${currency.symbol}${numAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

const formatNumberDefault = (amount, decimals = 2) => {
  const numAmount = parseFloat(amount) || 0;
  return numAmount.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

// ── Shared compact-format engine (used by both the default context and the live provider) ──
const _compactEngine = (numAmount, symbol, formatFull) => {
  const isNegative = numAmount < 0;
  const absAmount  = Math.abs(numAmount);
  const formattedFull = formatFull(numAmount);

  let display;
  if      (absAmount >= 1.0e+12) display = (absAmount / 1.0e+12).toFixed(1) + 'T';
  else if (absAmount >= 1.0e+9)  display = (absAmount / 1.0e+9).toFixed(1)  + 'B';
  else if (absAmount >= 1.0e+6)  display = (absAmount / 1.0e+6).toFixed(1)  + 'M';
  else if (absAmount >= 1.0e+3)  display = (absAmount / 1.0e+3).toFixed(1)  + 'K';
  else                           display = absAmount.toFixed(1);

  // Strip trailing .0 suffix (e.g. "1.0M" → "1M")
  display = display.replace(/\.0([TBMK])$/, '$1');

  return {
    display: `${isNegative ? '-' : ''}${symbol}${display}`,
    full: formattedFull,
    isCompact: true,
  };
};

const formatCompactDefault = (amount, currency = defaultCurrency, threshold = 10000000) => {
  const numAmount = parseFloat(amount) || 0;
  const formattedFull = formatAmountDefault(numAmount, currency);
  if (Math.abs(numAmount) < threshold) return { display: formattedFull, full: formattedFull, isCompact: false };
  return _compactEngine(numAmount, currency?.symbol || '$', (n) => formatAmountDefault(n, currency));
};

const defaultCurrencyContext = {
  baseCurrency: defaultCurrency,
  currencies: [defaultCurrency],
  loading: false,
  formatAmount: formatAmountDefault,
  formatNumber: formatNumberDefault,
  formatQuantity: (amount) => bankersRound(amount, CONFIG.QUANTITY_DECIMALS),
  formatCompact: formatCompactDefault,
  getCurrencySymbol: (currency = defaultCurrency) => currency?.symbol || '$',
  convertAmount: (amount, fromCurrency, toCurrency = defaultCurrency) => {
    if (!fromCurrency || !toCurrency || fromCurrency.id === toCurrency.id) {
      return amount;
    }
    const fromRate = parseFloat(fromCurrency.exchangeRate) || 1;
    const toRate = parseFloat(toCurrency.exchangeRate) || 1;
    return (parseFloat(amount) * fromRate) / toRate;
  },
  setBaseCurrency: () => {}
};

const CurrencyContext = createContext(defaultCurrencyContext);

export const useCurrency = () => {
  return useContext(CurrencyContext);
};

export const CurrencyProvider = ({ children }) => {
  const { user } = useAuth();
  const [baseCurrency, setBaseCurrency] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch company's base currency and all currencies
  useEffect(() => {
    const fetchCurrencyData = async () => {
      if (!user) {
        setLoading(false);        // ← add this
        setCurrencies([]);        // reset to safe defaults
        setBaseCurrency(null);
        return;
      }

      try {
        setLoading(true);

        // Get company data to find base currency
        const companyResponse = await companiesAPI.getCurrent();
        const company = companyResponse.data?.company || companyResponse.data;

        // Get all currencies
        const currenciesResponse = await currenciesAPI.getAll();
        const allCurrencies = currenciesResponse.data?.currencies || currenciesResponse.data || [];
        setCurrencies(allCurrencies);

        // Find base currency
        if (company?.baseCurrencyId) {
          const base = allCurrencies.find(c => c.id === company.baseCurrencyId);
          setBaseCurrency(base || allCurrencies.find(c => c.code === 'USD') || allCurrencies[0]);
        } else {
          // Default to USD or first available currency
          setBaseCurrency(allCurrencies.find(c => c.code === 'USD') || allCurrencies[0]);
        }
      } catch (error) {
        // Set default currency
        setBaseCurrency({ code: 'USD', symbol: '$', name: 'US Dollar' });
      } finally {
        setLoading(false);
      }
    };

    fetchCurrencyData();
  }, [user]);

  // Format amount with base currency
  const formatAmount = (amount, currency = baseCurrency) => {
    if (!currency || amount === null || amount === undefined) return '0.00';

    const roundedAmount = bankersRound(amount, CONFIG.CURRENCY_DECIMALS);
    return `${currency.symbol}${roundedAmount.toLocaleString('en-US', {
      minimumFractionDigits: CONFIG.CURRENCY_DECIMALS,
      maximumFractionDigits: CONFIG.CURRENCY_DECIMALS
    })}`;
  };

  // Format amount without currency symbol
  const formatNumber = (amount, decimals = CONFIG.CURRENCY_DECIMALS) => {
    const roundedAmount = bankersRound(amount, decimals);
    return roundedAmount.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  const formatQuantity = (amount) => {
    const roundedQty = bankersRound(amount, CONFIG.QUANTITY_DECIMALS);
    return roundedQty.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: CONFIG.QUANTITY_DECIMALS
    });
  };

  const formatCompact = (amount, currency = baseCurrency, threshold = 10000000) => {
    const numAmount = parseFloat(amount) || 0;
    const formattedFull = formatAmount(numAmount, currency);
    if (Math.abs(numAmount) < threshold) return { display: formattedFull, full: formattedFull, isCompact: false };
    return _compactEngine(numAmount, currency?.symbol || '$', (n) => formatAmount(n, currency));
  };

  // Get currency symbol.
  // Accepts: a currency object {symbol}, a UUID string (currencyId), or an ISO code string.
  const getCurrencySymbol = (currency = baseCurrency) => {
    if (!currency) return baseCurrency?.symbol || '$';
    // Already a full object
    if (typeof currency === 'object') return currency.symbol || baseCurrency?.symbol || '$';
    // String: look up by id or code in the currencies list
    if (typeof currency === 'string' && currency) {
      const found = currencies.find(c => c.id === currency || c.code === currency);
      if (found?.symbol) return found.symbol;
    }
    return baseCurrency?.symbol || '$';
  };

  // Convert amount between currencies (basic implementation)
  const convertAmount = (amount, fromCurrency, toCurrency = baseCurrency) => {
    if (!fromCurrency || !toCurrency || fromCurrency.id === toCurrency.id) {
      return amount;
    }

    // Basic conversion using exchange rates
    const fromRate = parseFloat(fromCurrency.exchangeRate) || 1;
    const toRate = parseFloat(toCurrency.exchangeRate) || 1;

    return (parseFloat(amount) * fromRate) / toRate;
  };

  const value = {
    baseCurrency,
    currencies,
    loading,
    formatAmount,
    formatNumber,
    formatQuantity,
    formatCompact,
    getCurrencySymbol,
    convertAmount,
    setBaseCurrency
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyContext;
